import { API_BASE } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";

/**
 * Single source of truth for admin file uploads.
 *
 * - Auto-compresses images >400 KB before upload (iPhone photos are typically
 *   3–12 MB; production ingress proxies cap multipart bodies at ~1 MB which
 *   is the root cause of "Upload failed" toasts on the deployed site).
 * - Surfaces the real server error (413, 401, etc.) so the toast tells the
 *   admin exactly what went wrong instead of a generic message.
 * - Returns an absolute URL that lives under our own domain so it never
 *   breaks if the user later cancels their old WordPress hosting.
 */
export async function adminUpload(file) {
  if (!file) throw new Error("No file selected");
  let payload = file;
  try {
    payload = await compressImage(file);
  } catch {
    payload = file; // best-effort
  }
  const fd = new FormData();
  fd.append("file", payload, payload.name || "upload.jpg");
  const token = localStorage.getItem("fic_token");
  const r = await fetch(`${API_BASE}/admin/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!r.ok) {
    let detail = `${r.status}`;
    try {
      const body = await r.json();
      detail = body?.detail || detail;
    } catch { /* ignore */ }
    if (r.status === 413 || /too large/i.test(String(detail))) {
      throw new Error("Image too large even after compression. Try a smaller photo.");
    }
    if (r.status === 507 || /quota/i.test(String(detail))) {
      throw new Error(detail || "Storage quota exceeded — contact Emergent Support to upgrade your object storage.");
    }
    if (r.status === 401 || r.status === 403) {
      throw new Error("Session expired — log out and back in, then retry.");
    }
    throw new Error(`Upload failed (${detail})`);
  }
  const d = await r.json();
  return `${window.location.origin.replace(/\/$/, "")}${d.url}`;
}
